import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { TabViewTabChangeParams } from 'primereact/tabview'
import { useTranslation } from 'react-i18next'
 
// local imports
import { IAllFirmList } from 'types/FirmType'
import { IEmployeeSummary } from 'types/Employee'
import { ChartItems, INotificationChartData } from 'types/DashboardType'
import useDidMountEffect from 'utils/useDidMountEffect'
import AuthUtil from 'utils/AuthUtil'
import DateUtil from 'utils/DateUtil'
import DashboardService from 'service/DashboardService'
import LocationService from 'service/LocationService'
 
const fetchSumOfKey = (arr: any[], property: string) => {
  return arr.map((item: any) => item[property]).reduce((prev: any, curr: any) => prev + curr, 0)
}
 
const useGetChartData = (firmList: any, userInfo: any) => {
  const { t } = useTranslation()
  const employee = AuthUtil.getEmployeeSummary() as IEmployeeSummary
  const [locationList, setLocationList] = useState(
    userInfo !== 'LocationAdmin' || firmList.length > 1
      ? [{ id: null, name: `(${t('input-texts.all-locations')})` }]
      : []
  )
  const [selectedCompany, setSelectedCompany] = useState<any[]>(
    userInfo !== 'LocationAdmin' || firmList.length > 1 ? [...firmList] : [firmList[0]]
  )
  const initialSelectedLocation =
    userInfo !== 'LocationAdmin' || firmList.length > 1
      ? { id: null, name: `(${t('input-texts.all-locations')})` }
      : locationList[0]
  const [selectedLocation, setSelectedLocation] = useState<any>(initialSelectedLocation)
  const [maxStartDate, setMaxStartDate] = useState<Date | undefined>()
  const [maxEndDate, setMaxEndDate] = useState<Date | undefined>()
  const [startDate, setStartDate] = useState<Date | any | undefined>(new Date())
  const [endDate, setEndDate] = useState<Date | any | undefined>(new Date())
  const [notificationResponse, setNotificationResponse] = useState<INotificationChartData>()
  const [notificationFirmUser, setNotificationFirmUser] = useState<ChartItems>()
  const [occupancy, setOccupancy] = useState<ChartItems>()
  const [channelResponse, setChannelResponse] = useState<ChartItems>()
  const [companyFullness, setCompanyFullness] = useState<ChartItems>()
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [prevTabIndices, setPrevTabIndices] = useState<Set<number>>(new Set())
  const [showLoader, setShowLoader] = useState<boolean>(false)
  const [pageLoading, setPageLoading] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [excelData, setExcelData] = useState<any>()
 
  useEffect(() => {
    let futureDate = new Date()
    let futureDayCount = Number(employee.futureOrientedDayCount)
    futureDate.setDate(futureDate.getDate() + futureDayCount)
    setMaxStartDate(futureDate)
    setMaxEndDate(futureDate)
  }, [employee.futureOrientedDayCount])
 
  const [checkedValue, setCheckedValue] = useState(['Dedicated'])
 
  useDidMountEffect(() => {
    if (startDate > endDate) setEndDate(startDate)
  }, [startDate])
 
  useDidMountEffect(() => {
    if (endDate < startDate) setStartDate(endDate)
  }, [endDate])
 
  const getDashboardData = (
    companies: any,
    location: any,
    index: number,
    loadingFunc?: Dispatch<SetStateAction<boolean>>
  ) => {
    let idList = companies.map((item: IAllFirmList) => item.id)
    const request = {
      FirmIdList: idList,
      StartDate: DateUtil.formatDateToString(startDate),
      EndDate: DateUtil.formatDateToString(endDate),
    }
    const fullnessRequest = {
      FirmIdList: idList,
      LocationId: location && location.id,
      StartDate: DateUtil.formatDateToString(startDate),
      EndDate: DateUtil.formatDateToString(endDate),
      IsShared: index === 2,
    }
    loadingFunc?.(true)
    setShowError(false)
    if (index === 0) {
      DashboardService.getDashboardNotificationChart(request)
        .then((resp: any) => {
          // const array =
          //   companies.length !== firmList.length ? resp.data.NotificationLocationData : resp.data.NotificationFirmData
          const array = resp.data.NotificationFirmData
          setNotificationResponse(resp.data)
          setExcelData((prev: any) => ({ ...prev, notification: array }))
        })
        .catch((e) => setShowError(true))
        .finally(() => loadingFunc?.(false))
    } else if (index === 1) {
      const requestFirmUser = {
        FirmIdList: idList,
        StartDate: DateUtil.formatDateToString(startDate),
        EndDate: DateUtil.formatDateToString(endDate),
        IncludedDedicatedTable: checkedValue.indexOf('Dedicated') !== -1,
      }
      DashboardService.getNotificationFirmUser(requestFirmUser)
        .then((resp: any) => {
          // const array =
          //   companies.length !== firmList.length ? resp.data.notificationLocationData : resp.data.notificationFirmData
          const array = resp.data.notificationFirmData
          setExcelData((prev: any) => ({ ...prev, personal: array }))
 
          setNotificationFirmUser({
            doughnutItems: {
              datasets: [
                fetchSumOfKey(resp.data.notificationFirmData, 'officeReservationCount'),
                fetchSumOfKey(resp.data.notificationFirmData, 'sharedOfficeReservationCount'),
                fetchSumOfKey(resp.data.notificationFirmData, 'homeOfficeReservationCount'),
              ],
              total: fetchSumOfKey(resp.data.notificationFirmData, 'totalReservationCount'),
              totalPercentage: 0,
            },
            stackedItems: {
              labels: array.map((item: any) => {
                const words = item.firmName.split(' ')
                // const words =
                //   companies.length !== firmList.length ? item.locationName.split(' ') : item.firmName.split(' ')
                if (words.length > 3) {
                  return words.join(' ').match(/\b[\w']+(?:[^\w\n]+[\w']+){0,2}\b/g)
                } else {
                  return words.join(' ')
                }
              }),
              count: [
                array.map((item: any) => {
                  return item.officeReservationCount
                }),
                array.map((item: any) => {
                  return item.sharedOfficeReservationCount
                }),
                array.map((item: any) => {
                  return item.homeOfficeReservationCount
                }),
              ],
              percentage: [],
            },
          })
        })
        .catch((e) => setShowError(true))
        .finally(() => loadingFunc?.(false))
    } else if (index === 2) {
      DashboardService.getDashboardOccupancy(fullnessRequest)
        .then((resp: any) => {
          const array =
            !!location?.id || companies.length === 1 ? resp.data.occupancyLocationList : resp.data.occupancyFirmList
          // const array =
          //   companies.length !== firmList.length ? resp.data.occupancyLocationList : resp.data.occupancyFirmList
          setExcelData((prev: any) => ({ ...prev, sharedOccupancy: array }))
          setOccupancy({
            doughnutItems: {
              totalCapacity: fetchSumOfKey(resp.data.occupancyFirmList, 'totalCapacity'),
              datasets: [
                fetchSumOfKey(resp.data.occupancyFirmList, 'incomingCount'),
                fetchSumOfKey(resp.data.occupancyFirmList, 'notComingCount'),
              ],
              total: fetchSumOfKey(resp.data.occupancyFirmList, 'reservationCount'),
              totalPercentage: Math.round(
                (fetchSumOfKey(resp.data.occupancyFirmList, 'reservationCount') /
                  fetchSumOfKey(resp.data.occupancyFirmList, 'totalCapacity')) *
                  100
              ),
            },
            stackedItems: {
              labels: array.map((item: any) => {
                const words = !!location?.id || companies.length === 1 ? item.locationName.split(' ') : item.firmName.split(' ')
                // const words =
                //   companies.length !== firmList.length ? item.locationName.split(' ') : item.firmName.split(' ')
                if (words.length > 3) {
                  return words.join(' ').match(/\b[\w']+(?:[^\w\n]+[\w']+){0,2}\b/g)
                } else {
                  return words.join(' ')
                }
              }),
              count: [
                array.map((item: any) => {
                  return item.incomingCount
                }),
                array.map((item: any) => {
                  return item.notComingCount
                }),
              ],
              percentage: [
                array.map((item: any) => {
                  return item.incomingPercentage
                }),
                array.map((item: any) => {
                  return item.notComingPercentage
                }),
              ],
            },
          })
        })
        .catch((e) => setShowError(true))
        .finally(() => loadingFunc?.(false))
    } else if (index === 3) {
      DashboardService.getDashboardChannel(request)
        .then((resp) => {
          // const array = companies.length === firmList.length ? resp.data.channelFirmList : resp.data.channelLocationList
          const array = resp.data.channelFirmList
          setExcelData((prev: any) => ({ ...prev, channel: array }))
          setChannelResponse({
            doughnutItems: {
              total: fetchSumOfKey(resp.data.channelFirmList, 'totalReservationCount'),
              totalPercentage: 0,
              datasets: [
                fetchSumOfKey(resp.data.channelFirmList, 'iosReservationCount'),
                fetchSumOfKey(resp.data.channelFirmList, 'androidReservationCount'),
                fetchSumOfKey(resp.data.channelFirmList, 'huaweiReservationCount'),
                fetchSumOfKey(resp.data.channelFirmList, 'webReservationCount'),
              ],
            },
            stackedItems: {
              labels: array.map((item: any) => {
                const words = item.firmName.split(' ')
                // const words =
                //   companies.length !== firmList.length ? item.locationName.split(' ') : item.firmName.split(' ')
                if (words.length > 3) {
                  return words.join(' ').match(/\b[\w']+(?:[^\w\n]+[\w']+){0,2}\b/g)
                } else {
                  return words.join(' ')
                }
              }),
              count: [
                array.map((item: any) => item.iosReservationCount),
                array.map((item: any) => item.androidReservationCount),
                array.map((item: any) => item.huaweiReservationCount),
                array.map((item: any) => item.webReservationCount),
              ],
              percentage: [
                array.map((item: any) => item.iosReservationPercentage),
                array.map((item: any) => item.androidReservationPercentage),
                array.map((item: any) => item.huaweiReservationPercentage),
                array.map((item: any) => item.webReservationPercentage),
              ],
            },
          })
        })
        .catch((e) => setShowError(true))
        .finally(() => loadingFunc?.(false))
    } else if (index === 4) {
      DashboardService.getDashboardOccupancy(fullnessRequest)
        .then((resp) => {
          const array =
            !!location?.id || companies.length === 1 ? resp.data.occupancyLocationList : resp.data.occupancyFirmList
          // const array =
          //   companies.length !== firmList.length ? resp.data.occupancyLocationList : resp.data.occupancyFirmList
          setExcelData((prev: any) => ({ ...prev, firmOccupancy: array }))
          setCompanyFullness({
            doughnutItems: {
              totalCapacity: fetchSumOfKey(resp.data.occupancyFirmList, 'totalCapacity'),
              datasets: [],
              total: fetchSumOfKey(resp.data.occupancyFirmList, 'reservationCount'),
              totalPercentage: Math.round(
                (fetchSumOfKey(resp.data.occupancyFirmList, 'reservationCount') /
                  fetchSumOfKey(resp.data.occupancyFirmList, 'totalCapacity')) *
                  100
              ),
            },
            stackedItems: {
              labels: array.map((item: any) => {
                const words = !!location?.id || companies.length === 1  ? item.locationName.split(' ') : item.firmName.split(' ')
                // const words =
                //   companies.length !== firmList.length ? item.locationName.split(' ') : item.firmName.split(' ')
                if (words.length > 3) {
                  return words.join(' ').match(/\b[\w']+(?:[^\w\n]+[\w']+){0,2}\b/g)
                } else {
                  return words.join(' ')
                }
              }),
              count: [
                array.map((item: any) => {
                  // selectedCompany.length !== firmList.length ? item.reservationPerDay :
                  return item.reservationCount
                }),
              ],
              percentage: [
                array.map((item: any) => {
                  return item.occupancyPercentage
                }),
              ],
            },
          })
        })
        .catch((e) => setShowError(true))
        .finally(() => loadingFunc?.(false))
    } else loadingFunc?.(false)
  }
 
  const getAllLocationByFirmAndType = (companies: any) => {
    let idList = companies.map((item: any) => Number(item.id))
    const request = {
      firmList: idList,
      hasSharedOffice: tabIndex === 2,
    }
    LocationService.GetAllLocationByFirmAndType(request).then((resp) => {
      setLocationList(resp.data)
    })
  }
 
  useDidMountEffect(() => {
    getDashboardData(selectedCompany, selectedLocation, tabIndex, setShowLoader)
    setPrevTabIndices(new Set())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, checkedValue])
 
  useEffect(() => {
    getDashboardData(selectedCompany, selectedLocation, tabIndex, setPageLoading)
    getAllLocationByFirmAndType(selectedCompany)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
 
  useDidMountEffect(() => {
    if (userInfo !== 'LocationAdmin' || firmList.length > 1) {
      setSelectedLocation({ Id: null, Name: `(${t('input-texts.all-locations')})` })
    }
  }, [selectedCompany, locationList, t('input-texts.all-locations')])
 
  /* Handle Tab Change */
  const handleTabChange = (e: TabViewTabChangeParams) => {
    const newIndex = e.index
    if (newIndex === tabIndex) return
    if (prevTabIndices.has(newIndex)) {
      setTabIndex(newIndex)
      setPrevTabIndices(prevTabIndices.add(tabIndex))
      return
    }
    setPrevTabIndices(prevTabIndices.add(tabIndex))
    setTabIndex(newIndex)
 
    const selectedLoc =
      firmList.length > 1 || userInfo !== 'LocationAdmin'
        ? { Id: null, Name: `(${t('input-texts.all-locations')})` }
        : locationList[0]
    setSelectedLocation(selectedLoc)
 
    getDashboardData(selectedCompany, selectedLoc, e.index, !prevTabIndices.has(newIndex) ? setShowLoader : undefined)
  }
 
  /* Firm Dropdown change handler */
  const handleFirmChange = (companies: any): void => {
    getAllLocationByFirmAndType(companies)
    getDashboardData(companies, initialSelectedLocation, tabIndex, setShowLoader)
    setPrevTabIndices(new Set())
  }
 
  /* Location Dropdown change handler */
  const handleLocationChange = (companies: any, location: any): void => {
    getDashboardData(companies, location, tabIndex, setShowLoader)
    setPrevTabIndices(new Set())
  }
 
  return {
    excelData,
    channelResponse,
    companyFullness,
    maxEndDate,
    maxStartDate,
    notificationFirmUser,
    notificationResponse,
    occupancy,
    checkedValue,
    setCheckedValue,
    showError,
    showLoader,
    tabIndex,
    endDate,
    setEndDate,
    setStartDate,
    startDate,
    handleTabChange,
    selectedCompany,
    selectedLocation,
    setSelectedCompany,
    setSelectedLocation,
    locationList,
    pageLoading,
    handleFirmChange,
    handleLocationChange,
  }
}
 
export default useGetChartData